import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ConditionOperator, HasAccessConfig } from '../models';
import { AreaAccessService } from '../services/area-access.service';

@Directive({
    selector: '[hasAccess]',
    standalone: false
})
export class HasAccessDirective implements OnInit, OnDestroy {

  @Input() set hasAccess(config: HasAccessConfig) {
    this.config$.next(config);
  }

  @Input() public hasAccessElse?: TemplateRef<unknown>;

  private renderedTemplate: TemplateRef<unknown> = null;

  private readonly config$ = new BehaviorSubject<HasAccessConfig>(null);

  private readonly unsubscribe = new Subject<void>();

  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly areaAccessService: AreaAccessService
  ) { }

  ngOnInit(): void {
    this.config$.pipe(
      distinctUntilChanged((previous, current) => this.isSameConfig(previous, current)),
      switchMap(config => this.resolveAccess(config)),
      takeUntil(this.unsubscribe)
    ).subscribe(hasAccess => this.setElementView(hasAccess));
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private resolveAccess(config: HasAccessConfig): Observable<boolean> {
    const { meta, operations, area, mode = ConditionOperator.OR } = config || {};
    const properties = this.getOperationList(operations);

    if (!properties.length && !area) {
      return of(true);
    }

    const operationAccess = properties.length ? this.checkForOperationAccess(meta, properties) : null;

    // area access is resolved asynchronously, so avoid waiting for it when the operations already decide
    if (!area) {
      return of(operationAccess);
    }

    if (operationAccess === true && mode !== ConditionOperator.AND) {
      return of(true);
    }

    if (operationAccess === false && mode === ConditionOperator.AND) {
      return of(false);
    }

    return this.areaAccessService.checkForPermission(area).pipe(
      catchError(() => of(false)),
      map(areaAccess => {
        if (operationAccess === null) {
          return areaAccess;
        }

        return mode === ConditionOperator.AND
          ? operationAccess && areaAccess
          : operationAccess || areaAccess;
      })
    );
  }

  private checkForOperationAccess(meta: HasAccessConfig['meta'], properties: string[]): boolean {
    if (!meta) {
      return false;
    }

    return properties.every(property => {
      const permission = property.split('.').reduce((value, key) => value?.[key], meta.operations);

      if (permission === undefined) {
        console.error(`Access property "${property}" does not exist`);
        return true;
      }

      return permission;
    });
  }

  private setElementView(hasAccess: boolean): void {
    const templateRef = hasAccess ? this.templateRef : this.hasAccessElse || null;

    if (templateRef === this.renderedTemplate) {
      return;
    }

    this.viewContainer.clear();
    this.renderedTemplate = templateRef;

    if (templateRef) {
      this.viewContainer.createEmbeddedView(templateRef);
    }
  }

  private isSameConfig(previous: HasAccessConfig, current: HasAccessConfig): boolean {
    return previous?.meta === current?.meta
      && this.getOperationList(previous?.operations).join() === this.getOperationList(current?.operations).join()
      && previous?.area === current?.area
      && previous?.mode === current?.mode;
  }

  private getOperationList(operations: HasAccessConfig['operations']): string[] {
    if (!operations) {
      return [];
    }

    return Array.isArray(operations) ? operations : [operations];
  }

}
